// {{variable}} helpers shared by the editor, template list and API modal.
// The pattern matches the server's resolveVars() exactly: {{anything-but-}}}
// with the name trimmed, so what the UI reports as a variable is precisely
// what the API will substitute.
const VAR_RE = /\{\{([^}]+)\}\}/g;

export function varsInString(str) {
  if (typeof str !== 'string') return [];
  const out = [];
  let m;
  VAR_RE.lastIndex = 0;
  while ((m = VAR_RE.exec(str))) out.push(m[1].trim());
  return out;
}

// Walks the same prop shapes the server resolves: string props, arrays of
// strings, and arrays of objects with string values (e.g. list items).
function varsInProps(props) {
  const out = [];
  for (const val of Object.values(props || {})) {
    if (typeof val === 'string') {
      out.push(...varsInString(val));
    } else if (Array.isArray(val)) {
      for (const item of val) {
        if (typeof item === 'string') out.push(...varsInString(item));
        else if (item && typeof item === 'object') {
          for (const v of Object.values(item)) out.push(...varsInString(v));
        }
      }
    }
  }
  return out;
}

// Unique variable names used by a single widget.
export function widgetVariables(widget) {
  return [...new Set(varsInProps(widget?.props))];
}

// Unique variable names used anywhere in a template (background + widgets),
// in first-appearance order.
export function extractVariables(templateJson) {
  const t = templateJson || {};
  const out = [...varsInString(t.background)];
  for (const w of t.widgets || []) out.push(...varsInProps(w.props));
  return [...new Set(out)];
}

/* ---- "Turn fixed text into variables" helpers ---- */

// A variable name derived from the text it replaces: "Welcome to ACME!" ->
// "welcome_to_acme". Falls back to "text" for content with no usable chars.
export function slugifyVarName(str) {
  return String(str)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .split('_')
    .slice(0, 4)
    .join('_') || 'text';
}

// True for a string that is real fixed content worth converting (non-empty,
// not already containing a variable).
const isConvertible = (s) => typeof s === 'string' && s.trim() !== '' && !s.includes('{{');

// The text-bearing props we convert per widget type. Colors, URLs and HTML
// stay fixed — they can still be made variables by hand.
function convertibleEntries(widget) {
  const p = widget?.props || {};
  const out = [];
  if (widget?.type === 'text' && isConvertible(p.text)) out.push(['text']);
  if (widget?.type === 'list') {
    if (isConvertible(p.title)) out.push(['title']);
    (Array.isArray(p.items) ? p.items : []).forEach((it, i) => {
      if (isConvertible(it)) out.push(['items', i]);
      else if (it && typeof it === 'object') {
        for (const k of ['label', 'value']) if (isConvertible(it[k])) out.push(['items', i, k]);
      }
    });
  }
  return out;
}

// How many fixed strings across these widgets a bulk convert would touch.
export function countConvertibleStrings(widgets) {
  return (widgets || []).reduce((n, w) => n + convertibleEntries(w).length, 0);
}

// Base name for a list's variables, taken from its TITLE ("Departures" ->
// "departures"). Works whether the title is still fixed text or already a
// variable like {{departures_title}}; empty titles fall back to "list".
function listBaseName(title) {
  const raw = String(title || '').replace(/[{}]/g, '').trim();
  if (!raw) return 'list';
  return slugifyVarName(raw).replace(/_title$/, '');
}

// Convert a widget's fixed text into {{variables}}. A text widget is named
// after its content ("Welcome to ACME" -> {{welcome_to_acme}}); a list is
// named after its title so the API stays predictable: {{departures_title}},
// {{departures_item1}}, {{departures_item2_label}}/{{departures_item2_value}}…
// `used` (a Set) tracks names across the whole template so clashes get _2, _3…
// Returns a new widget, or the original if nothing was convertible.
export function convertWidgetStringsToVars(widget, used) {
  if (convertibleEntries(widget).length === 0) return widget;

  const unique = (base) => {
    let name = base;
    let i = 2;
    while (used.has(name)) name = `${base}_${i++}`;
    used.add(name);
    return name;
  };

  const props = { ...widget.props };

  if (widget.type === 'text') {
    props.text = `{{${unique(slugifyVarName(props.text))}}}`;
    return { ...widget, props };
  }

  // list
  const base = listBaseName(props.title);
  if (isConvertible(props.title)) props.title = `{{${unique(`${base}_title`)}}}`;
  if (Array.isArray(props.items)) {
    props.items = props.items.map((it, i) => {
      const n = i + 1;
      if (isConvertible(it)) return `{{${unique(`${base}_item${n}`)}}}`;
      if (it && typeof it === 'object') {
        const o = { ...it };
        if (isConvertible(o.label)) o.label = `{{${unique(`${base}_item${n}_label`)}}}`;
        if (isConvertible(o.value)) o.value = `{{${unique(`${base}_item${n}_value`)}}}`;
        return o;
      }
      return it;
    });
  }
  return { ...widget, props };
}
